import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useEndpoints } from "../../../api/ApiContext";
import type { NotificationList, NotificationRow, PartyType } from "../../../api/types";
import { useSession } from "../../../auth/AuthProvider";
import { homeFor } from "../../../auth/pages";
import { CommandError } from "../../../components/CommandError";
import { CursorListView } from "../../../components/CursorListView";
import { Icon } from "../../../components/Icon";
import { Badge, Button, Card, Checkbox, EmptyState, PageHeader } from "../../../components/ui";
import { formatLocal } from "../../../lib/datetime";
import { useCursorList } from "../../../lib/useCursorList";
import { OrgTabs } from "./orgCopy";

// Notifications (G-08; UX-DESIGN.md §4.4). GET /v1/notifications
// (org.go:668-735), POST /v1/notifications/{id}/read and /read-all
// (:740-781). The inbox is per member (RLS, 0062:413-415). A link is
// portal-relative and party-agnostic ("/org/approvals", "/sites/<id>",
// 0062's triggers), so it is resolved under this portal's home.
//
// Not built server-side: delivery history and any out-of-band channel
// (API-GAPS.md G-08), so this screen doesn't claim either.

export const UNREAD_KEY = ["notifications", "unread-count"] as const;

/** A notification's link inside this portal; null when it isn't a portal path. */
export function notificationHref(link: string | null, partyType: PartyType): string | null {
  if (!link || !/^\/[^/]/.test(link)) return null;
  return `${homeFor(partyType)}${link}`;
}

/** The inbox's unread count: GET /v1/notifications?unread=true&limit=1 (unread_count covers the whole inbox, org.go:681). */
function useUnreadCount() {
  const ep = useEndpoints();
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => ep.notifications.list({ unread: true, limit: 1 }),
    refetchInterval: 60_000,
    retry: false,
  });
}

/** The bell in the app shell. */
export function NotificationBell() {
  const session = useSession();
  const q = useUnreadCount();
  const count = q.data?.unread_count ?? 0;
  const label = q.data ? (count === 0 ? "Notifications, none unread" : `Notifications, ${count} unread`) : "Notifications";
  return (
    <Link to={`${homeFor(session.partyType)}/org/notifications`} className="bell-link" aria-label={label} title={label}>
      <Icon name="bell" />
      {count > 0 ? (
        <span className="bell-count" aria-hidden="true">
          {count > 99 ? "99+" : count}
        </span>
      ) : null}
    </Link>
  );
}

export function NotificationsPage() {
  const ep = useEndpoints();
  const session = useSession();
  const qc = useQueryClient();
  const home = homeFor(session.partyType);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const list = useCursorList<NotificationList, NotificationRow>(
    ["notifications", "list", unreadOnly],
    (cursor) => ep.notifications.list({ unread: unreadOnly, cursor }),
    (p) => p.notifications,
  );
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["notifications"] });
  const markRead = useMutation({ mutationFn: (id: string) => ep.notifications.markRead(id), onSuccess: invalidate });
  const markAll = useMutation({ mutationFn: () => ep.notifications.markAllRead(), onSuccess: invalidate });
  const unread = useUnreadCount().data?.unread_count;

  return (
    <div className="stack-lg">
      <PageHeader
        title="Notifications"
        description="Review outcomes, suspensions, spend freezes, key revocations, payouts, refunds, approvals and member changes that need your attention."
      />
      <OrgTabs home={home} />
      <Card
        title="Your notifications"
        subtitle="Only you see these. Email delivery isn't available on this deployment yet, so check here."
        actions={
          <Button size="sm" variant="secondary" onClick={() => markAll.mutate()} loading={markAll.isPending} disabled={unread === 0}>
            Mark all read
          </Button>
        }
      >
        <div className="stack">
          <Checkbox label="Unread only" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} />
          {markAll.isSuccess ? (
            <p className="small" role="status">
              {markAll.data.marked_read === 1 ? "1 notification marked read." : `${markAll.data.marked_read} notifications marked read.`}
            </p>
          ) : null}
          {markRead.isError ? <CommandError error={markRead.error} /> : null}
          {markAll.isError ? <CommandError error={markAll.error} /> : null}
          <CursorListView
            list={list}
            loadingLabel="Loading notifications"
            empty={
              <EmptyState icon="bell" title={unreadOnly ? "Nothing unread" : "No notifications yet"}>
                You'll be told here when something needs your attention.
              </EmptyState>
            }
          >
            {(rows) => (
              <ul className="stack" style={{ listStyle: "none", margin: 0, padding: 0 }} aria-label="Notifications">
                {rows.map((n) => {
                  const href = notificationHref(n.link, session.partyType);
                  return (
                    <li key={n.id} className="card stack-sm" aria-label={n.title}>
                      <div className="row-between">
                        <strong>{n.title}</strong>
                        {n.read_at ? <span className="xs muted">Read</span> : <Badge tone="info">Unread</Badge>}
                      </div>
                      {n.body ? <p className="small">{n.body}</p> : null}
                      <div className="row-between">
                        <span className="xs muted">{formatLocal(n.created_at)}</span>
                        <span className="row">
                          {href ? (
                            <Link to={href} onClick={() => (n.read_at ? undefined : markRead.mutate(n.id))}>
                              Open
                            </Link>
                          ) : null}
                          {!n.read_at ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => markRead.mutate(n.id)}
                              loading={markRead.isPending && markRead.variables === n.id}
                              aria-label={`Mark "${n.title}" read`}
                            >
                              Mark read
                            </Button>
                          ) : null}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CursorListView>
        </div>
      </Card>
    </div>
  );
}
